import { useEffect, useRef } from 'react';
import { cn } from '../../lib/cn';
import { createNetworkField } from './network-field';

/**
 * Fondo animado del acceso al ecosistema.
 *
 * Los colores NO estan escritos aqui: se leen de las variables CSS del tema
 * (`--field-*` en index.css), de modo que el campo se adapta solo al tema
 * claro o al oscuro sin duplicar la paleta.
 *
 * El canvas es puramente decorativo (`aria-hidden`), va detras del contenido
 * y no intercepta el puntero: no interfiere con el teclado, los lectores de
 * pantalla ni el foco del formulario.
 */
function readFieldColors(): Parameters<typeof createNetworkField>[1] {
  const styles = getComputedStyle(document.documentElement);
  const read = (name: string, fallback: string): string =>
    styles.getPropertyValue(name).trim() || fallback;

  return {
    colors: {
      node: read('--field-node', 'rgba(13, 14, 17, 0.38)'),
      link: read('--field-link', 'rgba(13, 14, 17, 1)'),
      packet: read('--field-packet', 'rgba(200, 21, 29, 1)'),
      packetGlow: read('--field-glow', 'rgba(200, 21, 29, 1)'),
      auroraA: read('--field-aurora-a', 'rgba(200, 21, 29, 0.09)'),
      auroraB: read('--field-aurora-b', 'rgba(13, 14, 17, 0.05)'),
    },
    linkAlpha: Number.parseFloat(read('--field-link-alpha', '0.16')),
  };
}

export function NetworkBackground({ className }: { className?: string }): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const field = createNetworkField(canvas, readFieldColors());
    return () => field.destroy();
  }, []);

  return (
    <div
      aria-hidden
      className={cn('pointer-events-none absolute inset-0 overflow-hidden', className)}
    >
      <canvas ref={canvasRef} className="h-full w-full" />

      {/* Rejilla tecnica muy tenue, desvanecida hacia los bordes. */}
      <div className="absolute inset-0 bg-grid-fade" />

      {/* Vinetas: centran la atencion en la tarjeta de acceso. */}
      <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-transparent to-background" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-background to-transparent" />
    </div>
  );
}
